import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import {
  Controller,
  useForm,
  type Control,
  type UseFormRegister,
  type FieldErrors,
} from 'react-hook-form'

import { SendTestEmailButton } from '@/components/features/email/SendTestEmailButton'
import { FormField } from '@/components/ui/FormField'
import { FormSection } from '@/components/ui/FormSection'
import { FormSubmitButton } from '@/components/ui/FormSubmitButton'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { StatusAlert } from '@/components/ui/StatusAlert'
import { TEMP_CLERK_LEVEL, DISTRICT_MANAGER_LEVEL } from '@/constants/pos-user-levels'
import { useLocations, useDistricts } from '@/hooks/use-locations'
import { usePosUserTypes } from '@/hooks/use-pos-users'
import { INPUT_FORM_CLASS as INPUT_CLASS } from '@/lib/ui/input-styles'
import { SELECT_FORM_CLASS as SELECT_CLASS } from '@/lib/ui/select-styles'
import {
  posUserFormSchema,
  type PosUserFormData,
  type PosUserFormInput,
} from '@/lib/validation/pos-user-schemas'
import { type Location, type District } from '@/models/domain/location'
import { type PosUser, type PosUserTypeSummary } from '@/models/domain/pos-user'

interface PosUserFormProps {
  mode: 'create' | 'edit'
  user?: PosUser
  onSubmit: (data: PosUserFormData) => Promise<void>
  isLoading?: boolean
  error?: string | null
  canWrite?: boolean
}

function EmailField({
  register,
  error,
  showTestEmail,
  emailValue,
}: {
  register: UseFormRegister<PosUserFormInput>
  error?: string
  showTestEmail: boolean
  emailValue: string
}) {
  return (
    <div className={showTestEmail ? 'md:col-span-2' : ''}>
      <FormField label="Email" error={error}>
        <div className="flex items-center gap-3">
          <input
            type="email"
            {...register('email')}
            className={`${INPUT_CLASS} ${showTestEmail ? 'md:w-1/2' : ''}`}
            placeholder="user@example.com"
          />
          {showTestEmail && <SendTestEmailButton email={emailValue} />}
        </div>
      </FormField>
    </div>
  )
}

function shouldShowTestEmail(
  mode: 'create' | 'edit',
  watchedLevel: number,
  emailValue: string
): boolean {
  if (mode !== 'edit') return false
  if (watchedLevel !== DISTRICT_MANAGER_LEVEL) return false
  return emailValue.length > 0
}

function BasicInfoSection({
  register,
  errors,
  mode,
  watchedLevel,
  emailValue,
}: {
  register: UseFormRegister<PosUserFormInput>
  errors: FieldErrors<PosUserFormInput>
  mode: 'create' | 'edit'
  watchedLevel: number
  emailValue: string
}) {
  const isEdit = mode === 'edit'
  const readonlyClass = isEdit ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : ''
  const showTestEmail = shouldShowTestEmail(mode, watchedLevel, emailValue)

  return (
    <FormSection title="Basic Information" description="Name, email, and identification">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <FormField label="User ID" error={errors.id?.message} required>
            <input
              type="number"
              {...register('id')}
              readOnly={isEdit}
              className={`${INPUT_CLASS} ${readonlyClass}`}
              placeholder="Enter user ID"
            />
          </FormField>
        </div>
        <EmailField
          register={register}
          error={errors.email?.message}
          showTestEmail={showTestEmail}
          emailValue={emailValue}
        />
        <FormField label="First name" error={errors.first_name?.message} required>
          <input
            type="text"
            {...register('first_name')}
            className={INPUT_CLASS}
            placeholder="First name"
          />
        </FormField>
        <FormField label="Last name" error={errors.last_name?.message} required>
          <input
            type="text"
            {...register('last_name')}
            className={INPUT_CLASS}
            placeholder="Last name"
          />
        </FormField>
      </div>
    </FormSection>
  )
}

function DistrictField({
  control,
  error,
  sortedDistricts,
}: {
  control: Control<PosUserFormInput, unknown, PosUserFormData>
  error?: string
  sortedDistricts: District[]
}) {
  const districtOptions: SearchableSelectOption[] = useMemo(
    () => sortedDistricts.map((d) => ({ id: d.id, name: `${d.name} (${d.id})` })),
    [sortedDistricts]
  )

  return (
    <FormField label="District" htmlFor="district_id" error={error} required>
      <Controller
        name="district_id"
        control={control}
        render={({ field }) => (
          <SearchableSelect
            id="district_id"
            aria-label="District"
            options={districtOptions}
            value={field.value ?? ''}
            onChange={field.onChange}
            placeholder="Select a district"
          />
        )}
      />
    </FormField>
  )
}

function LocationField({
  control,
  error,
  sortedLocations,
}: {
  control: Control<PosUserFormInput, unknown, PosUserFormData>
  error?: string
  sortedLocations: Location[]
}) {
  const locationOptions: SearchableSelectOption[] = useMemo(
    () =>
      sortedLocations.map((loc) => ({
        id: String(loc.id),
        name: `${String(loc.id)} - ${loc.name ?? 'Unknown'}`,
      })),
    [sortedLocations]
  )

  return (
    <FormField label="Location" htmlFor="location_id" error={error} required>
      <Controller
        name="location_id"
        control={control}
        render={({ field }) => (
          <SearchableSelect
            id="location_id"
            aria-label="Location"
            options={locationOptions}
            value={field.value ?? ''}
            onChange={field.onChange}
            placeholder="Select a location"
          />
        )}
      />
    </FormField>
  )
}

function AccessSection({
  register,
  control,
  errors,
  sortedTypes,
  sortedLocations,
  sortedDistricts,
  showLocation,
  showDistrict,
}: {
  register: UseFormRegister<PosUserFormInput>
  control: Control<PosUserFormInput, unknown, PosUserFormData>
  errors: FieldErrors<PosUserFormInput>
  sortedTypes: PosUserTypeSummary[]
  sortedLocations: Location[]
  sortedDistricts: District[]
  showLocation: boolean
  showDistrict: boolean
}) {
  return (
    <FormSection title="Access & Assignment" description="Role level, PIN, and location assignment">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField label="Level" error={errors.level?.message} required>
          <select {...register('level')} className={SELECT_CLASS}>
            {sortedTypes.map((t) => (
              <option key={t.level} value={t.level}>
                {t.name} (Level {t.level})
              </option>
            ))}
            {sortedTypes.length === 0 && <option value={1}>Level 1</option>}
          </select>
        </FormField>
        <FormField label="PIN" error={errors.pin?.message} required>
          <input
            type="number"
            {...register('pin')}
            className={INPUT_CLASS}
            placeholder="Enter PIN"
          />
        </FormField>
        {showLocation && (
          <LocationField
            control={control}
            error={errors.location_id?.message}
            sortedLocations={sortedLocations}
          />
        )}
        {showDistrict && (
          <DistrictField
            control={control}
            error={errors.district_id?.message}
            sortedDistricts={sortedDistricts}
          />
        )}
      </div>
    </FormSection>
  )
}

function StatusSection({
  register,
  errors,
  showExpires,
}: {
  register: UseFormRegister<PosUserFormInput>
  errors: FieldErrors<PosUserFormInput>
  showExpires: boolean
}) {
  const checkboxClass =
    'w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500'

  return (
    <FormSection title="Status" description="Active state and expiration settings">
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <input type="checkbox" {...register('active')} id="active" className={checkboxClass} />
          <label htmlFor="active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Active
          </label>
        </div>
        {showExpires && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Expires" error={errors.expires?.message}>
              <input type="datetime-local" {...register('expires')} className={INPUT_CLASS} />
            </FormField>
            <div className="flex items-end pb-1">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  {...register('expires_eos')}
                  id="expires_eos"
                  className={checkboxClass}
                />
                <label
                  htmlFor="expires_eos"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Expires end of shift
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    </FormSection>
  )
}

const EMPTY_DEFAULTS: PosUserFormData = {
  id: 0,
  first_name: '',
  last_name: '',
  email: '',
  level: 1,
  pin: 0,
  location_id: '',
  district_id: '',
  active: true,
  expires: '',
  expires_eos: false,
}

function formatExpiresForInput(millis?: number): string {
  if (millis == null) return ''
  const d = new Date(millis)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`
}

function sortTypes(types: PosUserTypeSummary[]): PosUserTypeSummary[] {
  return [...types].sort((a, b) => a.level - b.level)
}

function sortLocationsByName(locs: Location[]): Location[] {
  return [...locs].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
}

function sortDistrictsByName(dists: District[]): District[] {
  return [...dists].sort((a, b) => a.name.localeCompare(b.name))
}

interface LevelVisibility {
  showLocation: boolean
  showDistrict: boolean
  showExpires: boolean
}

function getLevelVisibility(level: number): LevelVisibility {
  return {
    showLocation: level < DISTRICT_MANAGER_LEVEL,
    showDistrict: level === DISTRICT_MANAGER_LEVEL,
    showExpires: level === TEMP_CLERK_LEVEL,
  }
}

function useClearHiddenFields(
  visibility: LevelVisibility,
  setValue: (field: keyof PosUserFormData, value: string | boolean) => void
) {
  const { showLocation, showDistrict, showExpires } = visibility
  useEffect(() => {
    if (!showLocation) setValue('location_id', '')
    if (!showDistrict) setValue('district_id', '')
    if (!showExpires) {
      setValue('expires', '')
      setValue('expires_eos', false)
    }
  }, [showLocation, showDistrict, showExpires, setValue])
}

interface RestoreEditValuesOptions {
  mode: 'create' | 'edit'
  user: PosUser | undefined
  locations: Location[]
  districts: District[]
  showLocation: boolean
  showDistrict: boolean
  setValue: (
    field: keyof PosUserFormData,
    value: string,
    opts?: { shouldValidate: boolean }
  ) => void
}

function useRestoreEditValues(opts: RestoreEditValuesOptions) {
  const { mode, user, locations, districts, showLocation, showDistrict, setValue } = opts
  const locationsLoaded = locations.length > 0
  const districtsLoaded = districts.length > 0

  useEffect(() => {
    if (mode !== 'edit' || !user?.location_id) return
    if (locationsLoaded && showLocation) {
      setValue('location_id', user.location_id, { shouldValidate: true })
    }
  }, [mode, locationsLoaded, showLocation, user?.location_id, setValue])

  useEffect(() => {
    if (mode !== 'edit' || !user?.district_id) return
    if (districtsLoaded && showDistrict) {
      setValue('district_id', user.district_id, { shouldValidate: true })
    }
  }, [mode, districtsLoaded, showDistrict, user?.district_id, setValue])
}

function buildDefaultValues(user?: PosUser): PosUserFormData {
  if (!user) return EMPTY_DEFAULTS
  return {
    id: user.id,
    first_name: user.first_name ?? '',
    last_name: user.last_name ?? '',
    email: user.email ?? '',
    level: user.level,
    pin: user.pin ?? 0,
    location_id: user.location_id ?? '',
    district_id: user.district_id ?? '',
    active: user.active,
    expires: formatExpiresForInput(user.expires),
    expires_eos: user.expires_eos,
  }
}

function usePosUserFormSetup(mode: 'create' | 'edit', user?: PosUser) {
  const { data: userTypes = [] } = usePosUserTypes()
  const { data: locations = [] } = useLocations()
  const { data: districts = [] } = useDistricts()

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<PosUserFormInput, unknown, PosUserFormData>({
    resolver: zodResolver(posUserFormSchema),
    defaultValues: buildDefaultValues(user),
    mode: 'onChange',
  })

  const watchedLevel = parseInt(String(watch('level')), 10)
  const { showLocation, showDistrict, showExpires } = getLevelVisibility(watchedLevel)

  useClearHiddenFields({ showLocation, showDistrict, showExpires }, setValue)
  useRestoreEditValues({ mode, user, locations, districts, showLocation, showDistrict, setValue })

  return {
    register,
    control,
    handleSubmit,
    errors,
    isValid,
    watch,
    watchedLevel,
    showLocation,
    showDistrict,
    showExpires,
    sortedTypes: sortTypes(userTypes),
    sortedLocations: sortLocationsByName(locations),
    sortedDistricts: sortDistrictsByName(districts),
  }
}

export function PosUserForm({
  mode,
  user,
  onSubmit,
  isLoading = false,
  error,
  canWrite = true,
}: PosUserFormProps) {
  const {
    register,
    control,
    handleSubmit,
    errors,
    isValid,
    watch,
    watchedLevel,
    showLocation,
    showDistrict,
    showExpires,
    sortedTypes,
    sortedLocations,
    sortedDistricts,
  } = usePosUserFormSetup(mode, user)

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-5xl">
      <BasicInfoSection
        register={register}
        errors={errors}
        mode={mode}
        watchedLevel={watchedLevel}
        emailValue={watch('email') ?? ''}
      />
      <AccessSection
        register={register}
        control={control}
        errors={errors}
        sortedTypes={sortedTypes}
        sortedLocations={sortedLocations}
        sortedDistricts={sortedDistricts}
        showLocation={showLocation}
        showDistrict={showDistrict}
      />
      <StatusSection register={register} errors={errors} showExpires={showExpires} />
      {error && <StatusAlert type="error" message={error} />}
      <div className="flex items-center justify-end gap-3 pt-2">
        <FormSubmitButton
          isLoading={isLoading}
          isValid={isValid}
          canWrite={canWrite}
          writeGateResource="POS Users"
        >
          {mode === 'create' ? 'Create POS User' : 'Update POS User'}
        </FormSubmitButton>
      </div>
    </form>
  )
}
